import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { Search } from '@atlaskit/atlassian-navigation';
import Button from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import BulletList from '@atlaskit/icon/glyph/list';
import GridIcon from '@atlaskit/icon/glyph/media-services/grid';
import { Inline, Stack, xcss } from '@atlaskit/primitives';

import { NoPermsEmptyState, SearchEmptyState } from '../../common/assets';
import { VIEW_TYPE_LIST, VIEW_TYPE_THUMBNAIL } from '../../common/constants';
import { type ReportsHomePageProps, type ViewType } from '../../common/types';
import { ListView } from '../../common/ui/list-view';
import { ThumbnailView } from '../../common/ui/thumbnail-view';
import { useReportsHomePageController } from '../../controllers/use-reports-home-page-controller';

import { messages } from './messages';

const searchContainer = xcss({
	marginLeft: 'space.negative.250',
});

const filterStyles = xcss({
	gap: 'space.050',
});
const filtersAndViewContainer = xcss({
	justifyContent: 'space-between',
});
const extraFiltersContainer = xcss({
	gap: 'space.150',
});

export const ReportsHomePage = ({ reportsConfig, extraFilters }: ReportsHomePageProps) => {
	const { formatMessage } = useIntl();
	const { searchForReport, searchValue, filteredReportsConfig } =
		useReportsHomePageController(reportsConfig);
	const [viewType, setViewType] = useState<ViewType>(VIEW_TYPE_THUMBNAIL);

	const switchViews = useCallback(() => {
		if (viewType === VIEW_TYPE_LIST) {
			setViewType(VIEW_TYPE_THUMBNAIL);
			return;
		}
		setViewType(VIEW_TYPE_LIST);
	}, [setViewType, viewType]);

	const noAccess = reportsConfig.reduce(
		(accumulator, currentValue) => accumulator && !currentValue.access,
		true,
	);

	if (noAccess && reportsConfig.length > 0) {
		return (
			<EmptyState
				header={formatMessage(messages.noAccessEmptyStateHeader)}
				description={formatMessage(messages.noAccessEmptyStateDescription)}
				headingLevel={3}
				imageUrl={NoPermsEmptyState}
			/>
		);
	}

	return (
		<Stack>
			<Inline xcss={filtersAndViewContainer}>
				<Inline xcss={filterStyles}>
					<Inline xcss={searchContainer}>
						<Search
							onClick={searchForReport}
							placeholder={formatMessage(messages.searchPlaceholder)}
							tooltip={formatMessage(messages.searchPlaceholder)}
							label={formatMessage(messages.searchPlaceholder)}
							value={searchValue}
						/>
					</Inline>
					<Inline xcss={extraFiltersContainer}>
						{extraFilters.map((item) => (
							<Inline>{item}</Inline>
						))}
					</Inline>
				</Inline>
				<Inline>
					<Button
						onClick={switchViews}
						// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
						iconAfter={viewType === VIEW_TYPE_THUMBNAIL ? BulletList : GridIcon}
					>
						{viewType === VIEW_TYPE_THUMBNAIL
							? formatMessage(messages.swithcToListViewButton)
							: formatMessage(messages.swithcToCardViewButton)}
					</Button>
				</Inline>
			</Inline>
			<Stack>
				{filteredReportsConfig.length > 0 ? (
					<>
						{viewType === VIEW_TYPE_THUMBNAIL && (
							<ThumbnailView reportsConfig={filteredReportsConfig} />
						)}
						{viewType === VIEW_TYPE_LIST && <ListView reportsConfig={filteredReportsConfig} />}
					</>
				) : (
					<EmptyState
						header={formatMessage(messages.searchEmptyStateTitle)}
						description={formatMessage(messages.searchEmptyStateDescription)}
						headingLevel={3}
						imageUrl={SearchEmptyState}
					/>
				)}
			</Stack>
		</Stack>
	);
};
